import React from 'react';
import { cn } from '@/lib/utils';

// ─── Dialog (root) ──────────────────────────────────────────────────────────

interface DialogProps {
  /** Controls visibility. */
  open: boolean;
  /** Called when the user clicks the backdrop or presses Escape. */
  onClose: () => void;
  /** Dialog title rendered as an h2. */
  title: string;
  /** Dialog content (form, text, etc.). */
  children: React.ReactNode;
  /** Max-width class. Defaults to `max-w-lg`. */
  maxWidth?: string;
  /** Additional CSS classes on the panel. */
  className?: string;
}

/**
 * Modal dialog with backdrop blur.
 *
 * Uses a fixed overlay container to ensure consistent centering across browsers.
 *
 * @example
 * <Dialog open={isOpen} onClose={() => setIsOpen(false)} title="Create Team">
 *   <form onSubmit={handleSubmit} className="space-y-4">
 *     ...
 *     <DialogFooter>
 *       <Button variant="ghost" onClick={() => setIsOpen(false)}>Cancel</Button>
 *       <Button type="submit" isLoading={saving}>Create</Button>
 *     </DialogFooter>
 *   </form>
 * </Dialog>
 */
export function Dialog({ open, onClose, title, children, maxWidth = 'max-w-lg', className }: Readonly<DialogProps>) {
  if (!open) return null;

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
        tabIndex={-1}
      />
      <dialog
        open
        aria-label={title}
        onCancel={handleCancel}
        className={cn('relative m-0 w-full rounded-lg border bg-card p-6 text-foreground shadow-lg sm:p-8', maxWidth, className)}
      >
        <h2 className="mb-4 text-xl font-semibold">{title}</h2>
        {children}
      </dialog>
    </div>
  );
}

// ─── DialogFooter ───────────────────────────────────────────────────────────

interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Standard dialog footer with right-aligned buttons.
 *
 * @example
 * <DialogFooter>
 *   <Button variant="ghost" onClick={onClose}>Cancel</Button>
 *   <Button type="submit" isLoading={saving}>Save</Button>
 * </DialogFooter>
 */
export function DialogFooter({ children, className }: Readonly<DialogFooterProps>) {
  return (
    <div className={cn('flex justify-end gap-3 pt-4', className)}>
      {children}
    </div>
  );
}
